import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CalendarMonth, Clear } from "@mui/icons-material";
import moment from "moment";
import Axios from "axios";
import Select from "react-select";
import DateRangePicker from "react-daterange-picker";
import Sidebar from "../sidebar/sidebar";
import { companylogo } from "../../assets/images";
import { getSecureItem } from "../../utilsFunctions";
import { BaseUrl } from "../../Environment";

const ProfitLoss = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("sm"));
  const loggedinUser = getSecureItem("loggedinUser");
  const currency = getSecureItem("country")?.symbol;

  const [dateRangeModal, setDateRangeModal] = useState(false);
  const [dataPL, setDataPL] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [branchData, setBranchData] = useState([]);
  const [branchId, setBranchId] = useState(null);

  /** ✅ Utility to safely handle currency formatting */
  const formatCurrency = (amount) => {
    const safeAmount = isNaN(amount) || amount == null ? 0 : Number(amount);
    return `${currency} ${safeAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchBranches = async () => {
    try {
      const token = getSecureItem("token");
      const headers = {
        Authorization: "Bearer " + token,
      };

      const { data } = await Axios.get(`${BaseUrl}/branch/getAllBranches`, {
        params: { companyId: loggedinUser?.companyId },
        headers,
      });

      const branchData = loggedinUser?.userType == "superAdmin" ? data?.data : data?.data?.filter((item) => item?.branchId == loggedinUser?.branchId);

      setBranchData(branchData?.map(item => ({
        label: item.branchName,
        value: item.branchId
      })) || []);
    } catch (err) {
      console.error("Fetch Branches Error:", err);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  /** ✅ Fetch Profit & Loss */
  const getProfitLoss = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const companyId = getSecureItem("loggedinUserCompany");
      const loggedinUser = getSecureItem("loggedinUser");

      if (!token || !companyId) return;

      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        companyId,
        startDate: dateRange?.start || null,
        endDate: dateRange?.end || null,
        branchId:  loggedinUser?.userType == "superAdmin"?branchId?.value || null:branchId?.value || loggedinUser?.branchId || null
      };

      const response = await Axios.post(
        `${BaseUrl}/accounting/profitandloss`,
        payload,
        { headers }
      );
      if (response.status === 200 && response.data) {
        setDataPL(response.data);
      } else {
        setDataPL({});
      }
    } catch (error) {
      console.error("ProfitLoss API Error:", error);
      setDataPL({});
    }
  }, [dateRange?.end, branchId]);

  /** ✅ Auto-fetch whenever date range changes */
  useEffect(() => {
    getProfitLoss();
  }, [getProfitLoss]);

  /** ✅ Safely extract all values (avoiding NaN) */
  const totals = dataPL?.totals || {};
  const expenses = dataPL?.expenses || {};
  const breakdowns = dataPL?.breakdowns || {};

  const totalInterest = totals.totalInterestCollected || 0;
  const totalProcessing = totals.totalProcessingFees || 0;
  const totalRevenueFees = totals.totalRevenueFees || 0;
  const totalPayableFees = totals.totalPayableFees || 0;
  const totalOtherIncome = totals.totalOtherIncome || 0;
  const totalInvestmentRepayment = totals.totalInvestmentRepayment || 0;
  const directExp = expenses.summaryByType?.Direct || 0;
  const indirectExp = expenses.summaryByType?.Indirect || 0;

  const netOperatingIncome =
    totalInterest +
    totalRevenueFees +
    totalProcessing +
    totalOtherIncome -
    (
      directExp +
      indirectExp +
      totalInvestmentRepayment +
      totalPayableFees
    );

  /** ✅ Clear Date Filter */
  const handleClearDate = () => {
    setDateRange(null);
  };

  const revenueFees = Object.entries(
    dataPL?.feesDetailsBreakdown || {}
  ).filter(
    ([_, fee]) => fee?.accountingType === "revenue"
  );

  const payableFees = Object.entries(
    dataPL?.feesDetailsBreakdown || {}
  ).filter(
    ([_, fee]) => fee?.accountingType === "payable"
  );

  return (
    <>
      <Sidebar />
      <Box sx={{ ml: "73px", height: "100vh" }}>
        {/* Header */}
        <Stack
          direction={matches ? "column" : "row"}
          alignItems="center"
          justifyContent="space-between"
          sx={{ backgroundColor: "#fff", px: 2, height: "70px" }}
        >
          <img src={companylogo} width={200} height={40} alt="logo" />

          <Stack direction="row" spacing={1}>
            <div style={{width: "200px"}}>
              <Select
                options={branchData}
                value={branchId}
                onChange={(val) => setBranchId(val)}
                placeholder="Select Branch"
                isDisabled={(loggedinUser?.company?.planId == 3) ? false : true}
              />
            </div>
            <Button
              onClick={() => setDateRangeModal(true)}
              variant="outlined"
              startIcon={<CalendarMonth />}
              sx={{ maxWidth: 300 }}
            >
              {dateRange
                ? `${moment(dateRange.start).format("DD MMM")} - ${moment(
                    dateRange.end
                  ).format("DD MMM YYYY")}`
                : "Select Date Range"}
            </Button>

            {dateRange && (
              <Button
                onClick={handleClearDate}
                variant="outlined"
                color="error"
                startIcon={<Clear />}
              >
                Clear Filter
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Main Content */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Profit / Loss Statement */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Profit / Loss Statement
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography fontWeight="bold">
                              {dataPL?.fromDate
                                ? `${moment(dataPL.fromDate).format(
                                    "LL"
                                  )} - ${moment(dataPL.toDate).format("LL")}`
                                : "All Time"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">Amount</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {[
                          { label: "Revenue", isHeader: true },
                          {
                            label: "Interest Income",
                            value: totalInterest,
                            indent: true,
                          },
                          {
                            label: "Process Fee",
                            value: totalProcessing,
                            indent: true,
                          },
                          ...revenueFees.map(([key, fee]) => ({
                            label: key,
                            value: fee.amount,
                            indent: true,
                          })),
                          {
                            label: "Other Income",
                            value: totalOtherIncome,
                            indent: true,
                          },
                          { label: "Expenses", isHeader: true },
                          {
                            label: "Investment Repayment",
                            value: totalInvestmentRepayment,
                            indent: true,
                          },
                          ...payableFees.map(([key, fee]) => ({
                            label: key,
                            value: fee.amount,
                            indent: true,
                          })),
                          {
                            label: "Direct Expenses",
                            value: directExp,
                            indent: true,
                          },
                          {
                            label: "Indirect Expenses",
                            value: indirectExp,
                            indent: true,
                          },
                          {
                            label: "Net Operating Income",
                            value: netOperatingIncome,
                            bold: true,
                            highlightGreen: true,
                          },
                        ].map((row, index) =>
                          row.isHeader ? (
                            <TableRow key={index}>
                              <TableCell colSpan={2}>
                                <Typography fontWeight="bold">
                                  {row.label}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: row.highlightGreen
                                  ? "#e8f5e8"
                                  : "inherit",
                              }}
                            >
                              <TableCell sx={{ pl: row.indent ? 3 : 1 }}>
                                <Typography
                                  fontWeight={row.bold ? "bold" : "medium"}
                                >
                                  {row.label}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={row.bold ? "bold" : "normal"}
                                >
                                  {formatCurrency(row.value)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Detailed Breakdown */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Detailed Breakdown
                  </Typography>

                  <Stack spacing={2}>
                    {/* Other Income */}
                    <Typography fontWeight="bold" mt={1}>
                      Other Income
                    </Typography>
                    {breakdowns?.otherIncomeGrouped?.length ? (
                      breakdowns.otherIncomeGrouped.map((item, idx) => (
                        <Grid
                          key={idx}
                          container
                          justifyContent="space-between"
                          sx={{ pl: 2 }}
                        >
                          <Typography variant="body2">
                            {item?.IncomeType}
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(item?.totalAmount)}
                          </Typography>
                        </Grid>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ pl: 2, color: "text.secondary" }}
                      >
                        No data available
                      </Typography>
                    )}

                    <Box
                      sx={{ backgroundColor: "#f0f8ff", p: 2, borderRadius: 1 }}
                    >
                      <Grid container justifyContent="space-between">
                        <Typography fontWeight="bold">
                          Total Other Income
                        </Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(totalOtherIncome)}
                        </Typography>
                      </Grid>
                    </Box>

                    <Divider />

                    {/* Direct Expenses */}
                    <Typography fontWeight="bold" mt={1}>
                      Direct Expenses (E)
                    </Typography>
                    {expenses?.groupedByCategory
                      ?.filter((e) => e.type === "Direct")
                      ?.map((e, idx) => (
                        <Grid
                          key={idx}
                          container
                          justifyContent="space-between"
                          sx={{ pl: 2 }}
                        >
                          <Typography variant="body2">{e.category}</Typography>
                          <Typography variant="body2">
                            {formatCurrency(e.totalAmount)}
                          </Typography>
                        </Grid>
                      ))}

                    <Box
                      sx={{ backgroundColor: "#f0f8ff", p: 2, borderRadius: 1 }}
                    >
                      <Grid container justifyContent="space-between">
                        <Typography fontWeight="bold">
                          Gross Profit (G) = P - E
                        </Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(
                            totalInterest +
                              totalRevenueFees +
                              totalProcessing +
                              totalOtherIncome -
                              (directExp + indirectExp + totalInvestmentRepayment + totalPayableFees)
                          )}
                        </Typography>
                      </Grid>
                    </Box>

                    <Divider />

                    {/* Indirect Expenses */}
                    <Typography fontWeight="bold" mt={1}>
                      Indirect Expenses (O)
                    </Typography>
                    {expenses?.groupedByCategory
                      ?.filter((e) => e.type === "Indirect")
                      ?.map((e, idx) => (
                        <Grid
                          key={idx}
                          container
                          justifyContent="space-between"
                          sx={{ pl: 2 }}
                        >
                          <Typography variant="body2">{e.category}</Typography>
                          <Typography variant="body2">
                            {formatCurrency(e.totalAmount)}
                          </Typography>
                        </Grid>
                      ))}

                    {/* Net Income */}
                    <Box
                      sx={{
                        backgroundColor: "#e8f5e8",
                        p: 2,
                        borderRadius: 1,
                        border: "1px solid #c8e6c9",
                      }}
                    >
                      <Grid container justifyContent="space-between">
                        <Typography variant="h6" fontWeight="bold">
                          Net Income (N) = G - O
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="success.main"
                        >
                          {formatCurrency(netOperatingIncome)}
                        </Typography>
                      </Grid>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Date Range Modal */}
        <Dialog open={dateRangeModal} onClose={() => setDateRangeModal(false)}>
          <DialogTitle>Select Date</DialogTitle>
          <DialogContent>
            <DateRangePicker
              value={dateRange}
              onSelect={setDateRange}
              singleDateRange
            />
            <Stack spacing={1} mt={1}>
              <Typography fontSize={15} textAlign="center" variant="h6">
                Start Date:{" "}
                {dateRange?.start
                  ? dateRange.start.startOf("d").format("DD/MM/YYYY")
                  : "-"}
              </Typography>
              <Typography fontSize={15} textAlign="center" variant="h6">
                End Date:{" "}
                {dateRange?.end
                  ? dateRange.end.startOf("d").format("DD/MM/YYYY")
                  : "-"}
              </Typography>
            </Stack>
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default ProfitLoss;
